import {
  CANVAS_ZONES,
  DEFAULT_LAYOUT,
  FEATURE_KEYS,
  LAYOUT_PRESETS,
  LAYOUT_VERSION,
  MOBILE_ACTIONS,
  PRODUCT_INFO_FIELDS,
  ROOM_WIDGETS,
  ROOM_ZONES,
  TOPBAR_ZONES,
  BarStyle,
  MobileActionId,
  ProductInfoField,
  RoomWidgetId,
  RoomZone,
  VisualizerLayout,
  WidgetDisplay,
  WidgetPlacement,
} from './layout.schema';

/**
 * Turn whatever is stored (or nothing) into a complete, valid layout.
 *
 * The stored document can be partial (an older editor, a hand-written preset),
 * can name widgets this build does not know (a newer editor), and can hold
 * values out of range. None of that may break a shopper's page, so every field
 * falls back to the default on its own: one bad value costs that value, never
 * the layout.
 */

const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback);
const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);
const num = (v: unknown, min: number, max: number, fallback: number) =>
  (typeof v === 'number' && Number.isFinite(v) ? Math.round(Math.min(max, Math.max(min, v))) : fallback);
const text = (v: unknown, fallback: string, max = 120) => (typeof v === 'string' ? v.slice(0, max) : fallback);
const COLOR_RE = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%]+\)|hsla?\(\s*[\d.\s,%deg]+\)|transparent)$/i;
const color = (v: unknown): string | undefined => (typeof v === 'string' && COLOR_RE.test(v.trim()) ? v.trim() : undefined);

const BAR_STYLES: readonly BarStyle[] = ['attached', 'floating', 'glass'];
const DISPLAYS: readonly WidgetDisplay[] = ['icon-label', 'icon', 'label'];

export const PANEL_WIDTH_MIN = 300;
export const PANEL_WIDTH_MAX = 600;

function normalizeWidgets(input: unknown): WidgetPlacement[] {
  const defaults = DEFAULT_LAYOUT.room.desktop.widgets;
  const out: WidgetPlacement[] = [];
  const seen = new Set<string>();

  if (Array.isArray(input)) {
    for (const raw of input) {
      if (!isObj(raw)) continue;
      const id = raw.id as RoomWidgetId;
      if (!(ROOM_WIDGETS as readonly string[]).includes(id) || seen.has(id)) continue;
      const def = defaults.find((d) => d.id === id)!;
      const placement: WidgetPlacement = {
        id,
        zone: oneOf(raw.zone, ROOM_ZONES, def.zone),
        visible: bool(raw.visible, def.visible),
      };
      const display = raw.display ?? def.display;
      if (display) placement.display = oneOf(display, DISPLAYS, def.display ?? 'icon-label');
      if (id === 'productInfo') {
        const fields = Array.isArray(raw.fields)
          ? raw.fields.filter((f: unknown, i: number, all: unknown[]) =>
              (PRODUCT_INFO_FIELDS as readonly string[]).includes(f as string) && all.indexOf(f) === i)
          : def.fields;
        placement.fields = (fields?.length ? fields : def.fields) as ProductInfoField[];
      }
      seen.add(id);
      out.push(placement);
    }
  }

  // Anything the stored layout does not mention (a widget added after it was
  // saved) goes where the default puts it — in its zone, before the widget the
  // default has after it, so a new button lands among its neighbours rather
  // than after the menu.
  defaults.forEach((def, i) => {
    if (seen.has(def.id)) return;
    const placement = { ...def, ...(def.fields && { fields: [...def.fields] }) };
    const nextId = defaults.slice(i + 1).find((d) => d.zone === def.zone && out.some((o) => o.id === d.id && o.zone === def.zone))?.id;
    const at = nextId ? out.findIndex((o) => o.id === nextId) : -1;
    if (at >= 0) out.splice(at, 0, placement);
    else out.push(placement);
  });
  return out;
}

export function normalizeLayout(input: unknown): VisualizerLayout {
  const d = DEFAULT_LAYOUT;
  const src = isObj(input) ? input : {};
  const g = isObj(src.gallery) ? src.gallery : {};
  const gh = isObj(g.header) ? g.header : {};
  const room = isObj(src.room) ? src.room : {};
  const desk = isObj(room.desktop) ? room.desktop : {};
  const panel = isObj(desk.panel) ? desk.panel : {};
  const tabs = isObj(panel.tabs) ? panel.tabs : {};
  const mob = isObj(room.mobile) ? room.mobile : {};
  const feats = isObj(src.features) ? src.features : {};

  const actions: MobileActionId[] = Array.isArray(mob.actions)
    ? (mob.actions as unknown[]).filter((a, i, all): a is MobileActionId =>
        (MOBILE_ACTIONS as readonly string[]).includes(a as string) && all.indexOf(a) === i)
    : [...d.room.mobile.actions];

  const features = { ...d.features };
  for (const key of FEATURE_KEYS) features[key] = bool(feats[key], d.features[key]);

  const background = color(desk.canvas?.background);
  const galleryBackground = color(g.background);

  return {
    version: LAYOUT_VERSION,
    preset: typeof src.preset === 'string' ? src.preset.slice(0, 40) : d.preset,
    gallery: {
      header: {
        visible: bool(gh.visible, d.gallery.header.visible),
        align: oneOf(gh.align, ['split', 'center', 'left'] as const, d.gallery.header.align),
        showLogo: bool(gh.showLogo, d.gallery.header.showLogo),
        showSubtitle: bool(gh.showSubtitle, d.gallery.header.showSubtitle),
        title: text(gh.title, ''),
        subtitle: text(gh.subtitle, '', 200),
      },
      arrangement: oneOf(g.arrangement, ['masonry', 'grid', 'carousel'] as const, d.gallery.arrangement),
      columns: num(g.columns, 2, 6, d.gallery.columns),
      cardAspect: oneOf(g.cardAspect, ['4/3', '1/1', '3/4', '16/9'] as const, d.gallery.cardAspect),
      showNames: bool(g.showNames, d.gallery.showNames),
      filters: { visible: bool(g.filters?.visible, d.gallery.filters.visible) },
      upload: {
        visible: bool(g.upload?.visible, d.gallery.upload.visible),
        position: oneOf(g.upload?.position, ['first', 'last'] as const, d.gallery.upload.position),
        qr: bool(g.upload?.qr, d.gallery.upload.qr),
      },
      width: oneOf(g.width, ['narrow', 'normal', 'wide'] as const, d.gallery.width),
      ...(galleryBackground && { background: galleryBackground }),
    },
    room: {
      desktop: {
        widgets: normalizeWidgets(desk.widgets),
        panel: {
          side: oneOf(panel.side, ['left', 'right'] as const, d.room.desktop.panel.side),
          width: num(panel.width, PANEL_WIDTH_MIN, PANEL_WIDTH_MAX, d.room.desktop.panel.width),
          style: oneOf(panel.style, ['attached', 'floating'] as const, d.room.desktop.panel.style),
          start: oneOf(panel.start, ['auto', 'open'] as const, d.room.desktop.panel.start),
          collapsible: bool(panel.collapsible, d.room.desktop.panel.collapsible),
          tabs: {
            cart: bool(tabs.cart, d.room.desktop.panel.tabs.cart),
            favorites: bool(tabs.favorites, d.room.desktop.panel.tabs.favorites),
          },
          catalogColumns: num(panel.catalogColumns, 1, 4, d.room.desktop.panel.catalogColumns),
        },
        topBar: { style: oneOf(desk.topBar?.style, BAR_STYLES, d.room.desktop.topBar.style) },
        surfaceTools: { style: oneOf(desk.surfaceTools?.style, BAR_STYLES, d.room.desktop.surfaceTools.style) },
        canvas: { ...(background && { background }) },
      },
      mobile: {
        actions,
        maxVisible: num(mob.maxVisible, 1, 4, d.room.mobile.maxVisible),
        showChangeRoom: bool(mob.showChangeRoom, d.room.mobile.showChangeRoom),
      },
    },
    features,
  };
}

/** A preset as a complete layout. Unknown ids give the default. */
export function presetLayout(id: string): VisualizerLayout {
  const preset = LAYOUT_PRESETS[id] ?? {};
  const merged = deepMerge(DEFAULT_LAYOUT, preset);
  return normalizeLayout({ ...merged, preset: LAYOUT_PRESETS[id] ? id : 'classic' });
}

/** Objects merge key by key; arrays and values replace. */
function deepMerge(base: any, over: any): any {
  if (!isObj(base) || !isObj(over)) return over === undefined ? base : over;
  const out: Record<string, any> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = isObj(v) && isObj(base[k]) ? deepMerge(base[k], v) : v;
  }
  return out;
}

// ── Reading a layout ───────────────────────────────────────────────────────

/** The visible widgets of one zone, in order. */
export function zoneWidgets(layout: VisualizerLayout, zone: RoomZone): WidgetPlacement[] {
  return layout.room.desktop.widgets.filter((w) => w.zone === zone && w.visible);
}

export function widgetPlacement(layout: VisualizerLayout, id: RoomWidgetId): WidgetPlacement | undefined {
  return layout.room.desktop.widgets.find((w) => w.id === id);
}

export function isWidgetVisible(layout: VisualizerLayout, id: RoomWidgetId): boolean {
  return !!widgetPlacement(layout, id)?.visible;
}

/** Whether the desktop top bar has anything in it; an empty bar is not drawn. */
export function hasTopBar(layout: VisualizerLayout): boolean {
  return layout.room.desktop.widgets.some((w) => w.visible && TOPBAR_ZONES.includes(w.zone));
}

export const TOP_BAR_HEIGHTS: Record<BarStyle, number> = { attached: 68, floating: 80, glass: 80 };

/** Room the desktop top bar takes over the photo — what the viewers reserve. */
export function topBarSafeArea(layout: VisualizerLayout): number {
  return hasTopBar(layout) ? TOP_BAR_HEIGHTS[layout.room.desktop.topBar.style] : 0;
}

export function isCanvasZone(zone: RoomZone): boolean {
  return CANVAS_ZONES.includes(zone);
}

/** Panel width on the three desktop breakpoints (md / lg / xl). */
export function panelWidths(layout: VisualizerLayout): { md: number; lg: number; xl: number } {
  const lg = layout.room.desktop.panel.width;
  return { md: Math.max(280, lg - 100), lg, xl: lg + 50 };
}

// ── Editing a layout ───────────────────────────────────────────────────────

/**
 * Move a widget into `zone`, just before `beforeId` (or at the end of the zone
 * when that is null or not in the zone). Showing it is implied: dragging
 * something somewhere means wanting it there.
 */
export function moveWidget(
  layout: VisualizerLayout,
  id: RoomWidgetId,
  zone: RoomZone,
  beforeId: RoomWidgetId | null = null,
): VisualizerLayout {
  const widgets = layout.room.desktop.widgets;
  const moving = widgets.find((w) => w.id === id);
  if (!moving || beforeId === id) return layout;
  const rest = widgets.filter((w) => w.id !== id);
  const updated: WidgetPlacement = { ...moving, zone, visible: true };

  const before = beforeId ? rest.findIndex((w) => w.id === beforeId && w.zone === zone) : -1;
  let next: WidgetPlacement[];
  if (before >= 0) {
    next = [...rest.slice(0, before), updated, ...rest.slice(before)];
  } else {
    let last = -1;
    rest.forEach((w, i) => { if (w.zone === zone) last = i; });
    next = last >= 0 ? [...rest.slice(0, last + 1), updated, ...rest.slice(last + 1)] : [...rest, updated];
  }
  return { ...layout, room: { ...layout.room, desktop: { ...layout.room.desktop, widgets: next } } };
}

export function updateWidget(layout: VisualizerLayout, id: RoomWidgetId, patch: Partial<WidgetPlacement>): VisualizerLayout {
  const widgets = layout.room.desktop.widgets.map((w) => (w.id === id ? { ...w, ...patch, id: w.id } : w));
  return { ...layout, room: { ...layout.room, desktop: { ...layout.room.desktop, widgets } } };
}
